<template>
  <div class="dashboard-container">
    <div class="breadcrumb-minimal mb-3">
      <div class="flex align-items-center text-sm">
        <router-link
          to="/"
          class="text-500 hover:text-primary transition-colors"
        >
          <i class="pi pi-home mr-1"></i>
          MENÚ
        </router-link>
        <span class="mx-2 text-400">/</span>
        <span class="text-900 font-semibold">INICIO</span>
      </div>
    </div>

    <div
      class="welcome-banner bg-white shadow-1 border-round p-3 mb-4 flex align-items-center"
    >
      <i class="pi pi-user text-3xl mr-3" style="color: #002236"></i>
      <div>
        <div class="flex align-items-center flex-wrap">
          <span class="text-900 font-bold text-2xl mr-2"
            >Bienvenido: {{ user.name }}</span
          >
          <div
            class="role-badge flex align-items-center ml-2"
            :class="{
              'bg-green-100 text-green-800':
                !user.sucursal_nombre && !user.vicaria_nombre,
              'bg-blue-100 text-blue-800': user.sucursal_nombre,
              'bg-purple-100 text-purple-800': user.vicaria_nombre,
            }"
          >
            <i
              class="pi mr-2"
              :class="{
                'pi-shield': !user.sucursal_nombre && !user.vicaria_nombre,
                'pi-building': user.sucursal_nombre,
                'pi-star': user.vicaria_nombre,
              }"
            ></i>
            <span class="font-medium">
              Administra:
              <span v-if="user.sucursal_nombre">{{
                user.sucursal_nombre
              }}</span>
              <span v-else-if="user.vicaria_nombre">{{
                user.vicaria_nombre
              }}</span>
              <span v-else>Todas las Vicarias y Parroquias</span>
            </span>
          </div>
        </div>
        <div class="text-sm text-600 mt-1">
          <i class="pi pi-calendar mr-1"></i>
          {{
            new Date().toLocaleDateString("es-ES", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })
          }}
        </div>
      </div>
    </div>

    <div
      v-if="obtenerDatos"
      class="bg-yellow-50 p-2 text-center text-yellow-700 border-round shadow-sm text-lg font-medium"
    >
      <i class="pi pi-spinner pi-spin mr-2"></i> Cargando colectas activas...
    </div>
    <div v-else-if="campaniasActivas.length > 0" class="mb-4">
      <div
        class="text-center mb-3 flex justify-content-center align-items-center gap-2"
      >
        <i class="pi pi-bell animate-bell text-2xl text-red-700"></i>
        <h2 class="text-2xl font-bold text-green-800 m-0">COLECTAS ACTIVAS</h2>
      </div>
      <div class="grid">
        <div
          v-for="campania in campaniasActivas"
          :key="campania.id"
          class="col-12 lg:col-6"
        >
          <div
            class="campaign-card h-full p-3 flex align-items-center justify-content-between gap-2 shadow-1 border-round-2xl"
            :class="[
              parseFloat(campania.recolectado) > 0
                ? 'bg-green-50 border-green'
                : 'bg-red-50 border-red',
            ]"
          >
            <div class="flex-grow-1 flex align-items-center gap-1">
              <i
                class="pi pi-bullhorn text-2xl flex-shrink-0"
                :class="
                  parseFloat(campania.recolectado) > 0
                    ? 'text-green-700'
                    : 'text-red-700'
                "
              ></i>
              <div>
                <div class="text-lg font-bold line-height-1">
                  {{ campania.nombre }}
                </div>
                <div class="text-sm text-gray-600 mt-1 font-normal">
                  Del {{ formatFecha(campania.fecha_inicio) }} al
                  {{ formatFecha(campania.fecha_fin) }}
                </div>
              </div>
            </div>
            <div class="text-right flex-shrink-0">
              <div
                class="text-xl font-bold"
                :class="
                  parseFloat(campania.recolectado) > 0
                    ? 'text-green-700'
                    : 'text-red-700'
                "
              >
                Bs
                {{ convertirNumeroGermanicFormat(campania.recolectado ?? 0) }}
              </div>
              <div class="text-sm text-gray-600">
                {{ getTextoRecolectado() }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="grid mt-3">
      <div
        class="col-12 lg:col-4 xl:col-4"
        v-if="'Producto Crear' in authService.user.permissions"
      >
        <div class="compact-card">
          <div class="card-top flex justify-content-between align-items-center">
            <div>
              <div class="card-title">
                <i class="pi pi-box mr-2 text-sm" style="color: #002236"></i>
                <span class="font-medium text-sm">PRODUCTOS/SERVICIOS</span>
              </div>
              <div class="text-900 font-medium text-lg mt-1">
                Total: <strong>{{ productos }}</strong>
              </div>
            </div>
            <div
              class="icon-container"
              style="background-color: rgba(0, 34, 54, 0.1)"
            >
              <i class="pi pi-box" style="color: #002236"></i>
            </div>
          </div>
          <router-link
            to="/productos"
            class="card-link"
            v-tooltip.top="'Ver Listado Productos'"
            style="color: #002236"
          >
            <span>Ver detalles</span>
            <i class="pi pi-arrow-right text-xs"></i>
          </router-link>
        </div>
      </div>
      <div
        class="col-12 lg:col-8 xl:col-8"
        v-if="'Dashboard Productos+Vendidos' in authService.user.permissions"
      >
        <div class="compact-card">
          <div class="compact-header">
            <h3 class="compact-title">
              <i class="pi pi-chart-line" style="color: #002236"></i> OFRENDAS Y
              DONACIONES
            </h3>
          </div>
          <div class="compact-filters">
            <div class="compact-filter-group">
              <Calendar
                v-model="fecha_inicio"
                :showIcon="true"
                dateFormat="dd/mm/yy"
                class="compact-calendar"
                placeholder="Inicio"
                showButtonBar
              />
            </div>
            <div class="compact-filter-group">
              <Calendar
                v-model="fecha_fin"
                :showIcon="true"
                dateFormat="dd/mm/yy"
                class="compact-calendar"
                placeholder="Fin"
                showButtonBar
              />
            </div>
            <Button
              icon="pi pi-search"
              class="compact-search-button p-button-sm"
              @click="panel_necesario_auth()"
              :loading="obtenerDatos"
              v-tooltip.top="'Buscar'"
              style="background-color: #002236; border-color: #002236"
            />
          </div>
          <div class="flex">
            <div class="col-12">
              <div class="grid">
                <div class="compact-chart col-12 xl:col-6">
                  <div class="chart-mini-header" style="color: #002236">
                    <i class="pi pi-box"></i> Productos
                  </div>
                  <Chart
                    v-if="
                      productos_mas_vendidos &&
                      Object.keys(productos_mas_vendidos).length > 0
                    "
                    type="doughnut"
                    :data="chartDataProdServ"
                    :options="{ ...chartOptionsProdServ, height: 200 }"
                  />
                  <div v-else class="compact-empty">
                    <i class="pi pi-exclamation-circle"></i>
                  </div>
                </div>
                <div class="compact-chart col-12 xl:col-6">
                  <div class="chart-mini-header" style="color: #002236">
                    <i class="pi pi-wrench"></i> Servicios
                  </div>
                  <Chart
                    v-if="
                      servicios_mas_vendidos &&
                      servicios_mas_vendidos.length > 0
                    "
                    type="doughnut"
                    :data="chartDataServicios"
                    :options="{ ...chartOptionsServicios, height: 200 }"
                  />
                  <div v-else class="compact-empty">
                    <i class="pi pi-exclamation-circle"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// ... (Tu script y lógica de funciones se mantienen sin cambios)
import { FilterMatchMode } from "primevue/api";
import { useAuth } from "@/stores";
import ProductService from "@/service/ProductService";
import StockService from "@/service/StockService";
import UserService from "@/service/UserService";

export default {
  data() {
    return {
      user: {},
      productos: null,
      clientes: null,
      proveedores: null,
      stocks: [],
      enviando: false,
      productos_fecha_vence: [],
      productos_stock_minimo: [],
      stockClass: (data) => {
        if (data.cantidad == 0) {
          return "text-red-500";
        }
      },
      collapsed: false,
      productos_mas_vendidos: [],
      buscar: {
        global: {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
      },
      loading: false,
      obtenerDatos: false,
      total_comision: 0,
      fecha_inicio: new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        1
      ),
      fecha_fin: new Date(),
      servicios_mas_vendidos: [],
      chartDataProdServ: {},
      chartOptionsProdServ: {},
      chartDataServicios: {},
      chartOptionsServicios: {},
      campaniasActivas: [],
      campaniasActivasCount: 0,
    };
  },
  authService: null,
  productService: null,
  stockService: null,
  created() {
    this.authService = useAuth();
    this.productService = new ProductService();
    this.userService = new UserService();
    this.stockService = new StockService();
  },
  mounted() {
    this.user = { ...this.authService.user };
    this.panel_necesario_auth();
  },
  methods: {
    getTextoRecolectado() {
      if (!this.user.sucursal_id && !this.user.vicaria_id) {
        return "Total recolectado";
      }
      return "Aportado por tu parroquia";
    },
    descargarComisionesPDF() {
      let datos = {
        id: this.user.id,
        nombre_usuario: this.user.name,
      };
      this.userService.descargarComisionesPDF(datos);
    },
    toggle(event) {
      this.$refs.menu.toggle(event);
    },
    Obtenerproductos_fecha_vence() {
      if (this.collapsed == true) {
        this.enviando = false;
      } else {
        this.enviando = true;
        this.productService
          .getProductos_fecha_vence()
          .then((data) => {
            this.productos_fecha_vence = data.productos_fecha_vence ?? [];
            this.enviando = false;
          })
          .catch((error) => {
            console.log(error);
          });
      }
    },
    Obtenerproductos_stock_minimo() {
      if (this.collapsed == true) {
        this.enviando = false;
      } else {
        this.enviando = true;
        this.productService
          .getProductos_stock_minimo()
          .then((data) => {
            this.productos_stock_minimo = data.productos_stock_minimo;
            this.enviando = false;
          })
          .catch((error) => {
            console.log(error);
          });
      }
    },
    productosStockCero() {
      if (this.collapsed == true) {
        this.enviando = false;
      } else {
        this.enviando = true;
        this.stockService.productos_stock_cero().then((data) => {
          this.stocks = data.stocks;
          this.enviando = false;
        });
      }
    },
    panel_necesario_auth() {
      this.obtenerDatos = true;
      let datos = {
        id: this.user.id,
        panel: "panel_inicio",
        fecha_inicio: this.fecha_inicio,
        fecha_fin: this.fecha_fin,
      };
      this.userService.panel_necesario_auth(datos).then((data) => {
        this.campaniasActivas = data.datos.campanias ?? [];
        this.productos = data.datos.total_productos ?? 0;
        this.productos_mas_vendidos = data.datos.productos_mas_vendidos ?? 0;
        this.servicios_mas_vendidos = data.datos.servicios_mas_vendidos ?? 0;
        // Configuración de gráficos
        if (this.productos_mas_vendidos.length > 0) {
          this.chartDataProdServ = {
            labels: this.productos_mas_vendidos.map(
              (item) => item.descripcion + " (" + item.cantidad + ")"
            ),
            datasets: [
              {
                data: this.productos_mas_vendidos.map((item) => item.cantidad),
                backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56"],
                hoverBackgroundColor: ["#FF6384", "#36A2EB", "#FFCE56"],
              },
            ],
          };
        }
        this.chartOptionsProdServ = {
          responsive: true,
          maintainAspectRatio: false,
        };
        //verifico si hay servicios mas vendidos
        if (this.servicios_mas_vendidos.length > 0) {
          this.chartDataServicios = {
            labels: this.servicios_mas_vendidos.map(
              (item) => item.nombre_servicio + " (" + item.cantidad + ")"
            ),
            datasets: [
              {
                data: this.servicios_mas_vendidos.map((item) => item.cantidad),
                backgroundColor: ["#4BC0C0", "#FF9F40", "#9966FF"],
                hoverBackgroundColor: ["#4BC0C0", "#FF9F40", "#9966FF"],
              },
            ],
          };
          this.chartOptionsServicios = {
            responsive: true,
            maintainAspectRatio: false,
          };
        }
        this.obtenerDatos = false;
      });
    },
    convertirNumeroGermanicFormat(numero) {
      return new Intl.NumberFormat("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numero);
    },
    sinDecimal(numero) {
      return new Intl.NumberFormat("de-DE", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(numero);
    },
    formatFecha(fecha) {
      const opciones = { day: "numeric", month: "long" };
      return new Date(fecha).toLocaleDateString("es-BO", opciones);
    },
  },
};
</script>

<style scoped>
.dashboard-container {
  padding: 1rem;
  background-color: #ffffff;
}

/* Breadcrumb */
.breadcrumb-minimal {
  padding: 0.5rem 0;
}

/* Welcome Banner */
.welcome-banner {
  border-left: 4px solid #002236;
  transition: all 0.2s ease;
}

.welcome-banner:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.role-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.9rem;
}

/* Campaign Card */
.campaign-card {
  transition: transform 0.2s, box-shadow 0.2s;
  border-left: 5px solid;
}

.campaign-card.bg-red-50 {
  background-color: #fef2f2;
}

.campaign-card.bg-green-50 {
  background-color: #f0fdf4;
}

.campaign-card.border-red {
  border-left-color: #ef4444; /* Rojo */
}

.campaign-card.border-green {
  border-left-color: #22c55e; /* Verde */
}

.campaign-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

/* Tarjetas */
.compact-card {
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 0.75rem;
  transition: all 0.2s ease;
  background-color: white;
}

.compact-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-color: #d0d0d0;
}

.card-title {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: #555;
}

.icon-container {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.85rem;
  font-weight: 500;
  text-decoration: none;
  padding-top: 0.5rem;
  border-top: 1px solid #f0f0f0;
  transition: all 0.2s ease;
}

.card-link:hover {
  color: #001a2b !important;
}

.card-link i {
  transition: transform 0.2s ease;
}

.card-link:hover i {
  transform: translateX(2px);
}

/* Sección Más Vendidos */
.compact-dashboard-section {
  margin-top: 0.75rem;
}

.compact-header {
  margin-bottom: 0.75rem;
}

.compact-title {
  margin: 0;
  font-size: 1rem;
  color: #002236;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.compact-filters {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  align-items: center;
}

.compact-filter-group {
  flex: 1;
}

.compact-calendar .p-inputtext {
  padding: 0.35rem 0.5rem;
  font-size: 0.85rem;
}

.compact-search-button {
  height: 2.2rem;
  width: 2.2rem;
}

.compact-charts {
  display: flex;
  gap: 0.75rem;
}

.compact-chart {
  flex: 1;
  min-height: 220px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 0.75rem;
  background-color: white;
}

.chart-mini-header {
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-weight: 500;
}

.chart-mini-header i {
  font-size: 0.9rem;
}

.compact-empty {
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #aaa;
}

/* Responsive */
@media (max-width: 768px) {
  .compact-charts {
    flex-direction: column;
  }
  .welcome-banner {
    flex-direction: column;
    align-items: flex-start;
  }
  .role-badge {
    margin-top: 0.5rem;
    margin-left: 0 !important;
  }
}

.animate-bell {
  animation: ring 1.5s infinite;
}

@keyframes ring {
  0% {
    transform: rotate(0deg);
  }

  20% {
    transform: rotate(-10deg);
  }

  40% {
    transform: rotate(10deg);
  }

  60% {
    transform: rotate(-6deg);
  }

  80% {
    transform: rotate(6deg);
  }

  100% {
    transform: rotate(0deg);
  }
}
</style>
